import { observer } from 'mobx-react';
import { Maybe } from 'maybeasy';
import { Stripe } from '@stripe/stripe-js';
import { AddressElement, PaymentElement } from '@stripe/react-stripe-js';
import { PaymentCheckoutFormStore } from '../../../Common';
import PaymentCardCharge from '../PaymentCardCharge';
import PaymentCardSelect from '../PaymentCardSelect';
import { CheckoutKind } from '../CheckoutKind';
import PaymentCheckoutButton from '../PaymentCheckoutButton';

interface Props {
  store: PaymentCheckoutFormStore;
  price: Maybe<number>;
  stripe: Stripe;
  onSubmit: (e: React.FormEvent) => void;
  checkout: CheckoutKind;
}

function PaymentCardForm({ store, onSubmit, price, stripe, checkout }: Props) {
  switch (store.state.kind) {
    case 'waiting':
      return <PaymentCardSelect store={store} />;
    case 'ready-for-payment-confirmation':
    case 'payment-declined':
    case 'confirming-payment':
      switch (checkout) {
        case 'course': {
          return (
            <form className="flex h-full w-full flex-grow flex-col" onSubmit={onSubmit}>
              <PaymentElement className="flex flex-grow flex-col" />
              <div className="mt-12 sm:mt-4">
                <PaymentCardCharge price={price} />
                <PaymentCheckoutButton stripe={stripe} state={store.state} checkout={checkout} />
              </div>
            </form>
          );
        }
        case 'license': {
          // License purchase also collects the billing address (DVLP-7860): prices are
          // tax-inclusive and Finance reconciles tax by the buyer's location. Stripe
          // keeps it on the payment method, where Finance and the Salesforce sync
          // (DVLP-7913) read it; the platform stores no copy.
          //
          // Card plus address, with Stripe's inline errors, can outgrow the fixed
          // dialog on any screen, so the fields scroll and the charge and the confirm
          // button stay pinned in view.
          return (
            <form className="flex min-h-0 w-full flex-1 flex-col" onSubmit={onSubmit}>
              <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1 pb-1">
                <PaymentElement />
                <AddressElement className="mt-3" options={{ mode: 'billing' }} />
              </div>
              <div className="flex-none pt-4">
                <PaymentCardCharge price={price} />
                <PaymentCheckoutButton stripe={stripe} state={store.state} checkout={checkout} />
              </div>
            </form>
          );
        }
      }
  }
}

export default observer(PaymentCardForm);
